import React, { useCallback, useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { ClipboardList, FileDown, Loader2, Trash2, Snowflake, Gift } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { showPdfPreview } from "../components/PdfPreview";
import HaccpRegisterPdf, { EVENT_LABEL } from "../components/HaccpRegisterPdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const TONE = {
  DESTRUCTION: ["bg-[#FCEEEA] text-[#A04E2C] border-[#E8C1B4]", Trash2],
  FREEZE: ["bg-[#EAF1F6] text-[#2E6A8E] border-[#BCD3E3]", Snowflake],
  DON_PERSONNEL: ["bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]", Gift],
};
const CHANNELS = {
  DECHETS_ORGANIQUES: "Déchets organiques (collecte communale)", METHANISATION: "Méthanisation / biogaz",
  INCINERATION: "Incinération (UIOM)", PRESTATAIRE: "Prestataire agréé", AUTRE: "Autre",
};
const iso = (d) => d.toISOString().slice(0, 10);

const detail = (e) => (e.type === "FREEZE"
  ? `DDM ${fmtDate(e.ddm)}${e.notes ? ` — ${e.notes}` : ""}`
  : [CHANNELS[e.channel], e.ref && `Bon ${e.ref}`, e.notes].filter(Boolean).join(" — ") || e.reason);

export default function HaccpRegister() {
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [type, setType] = useState("all");
  const [data, setData] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    const { data: d } = await api.get("/haccp/register", { params: { from_date: from, to_date: to, ...(type !== "all" ? { type } : {}) } });
    setData(d);
  }, [from, to, type]);
  useEffect(() => { load().catch(() => setData({ events: [], totals: {} })); }, [load]);

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const enriched = { ...data, events: data.events.map((e) => ({ ...e, channel_label: CHANNELS[e.channel] })) };
      const blob = await pdf(<HaccpRegisterPdf data={enriched} period={`${fmtDate(from)} – ${fmtDate(to)}`} />).toBlob();
      showPdfPreview(blob, `Registre-HACCP-${from}-${to}.pdf`);
    } catch (e) {
      toast.error(`Erreur PDF : ${e?.message || e}`);
    } finally { setPdfBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="haccp-register-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8B3A1F]">Autocontrôle</p>
        <h1 className="font-heading text-3xl font-semibold flex items-center gap-2 mt-1"><ClipboardList className="w-7 h-7 text-[#1F4E3D]" /> Registre HACCP</h1>
        <p className="text-sm text-[#575A57] mt-1">Destructions (avec filière d&apos;élimination), congélations et dons au personnel — à présenter lors d&apos;un contrôle SCAV.</p>
      </div>
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div><label className="text-xs text-[#575A57]">Du</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="haccp-from" /></div>
            <div><label className="text-xs text-[#575A57]">Au</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="haccp-to" /></div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-48" data-testid="haccp-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les opérations</SelectItem>
                {Object.entries(EVENT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={exportPdf} disabled={!data || pdfBusy} variant="outline" data-testid="haccp-pdf">
              {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} PDF imprimable
            </Button>
            {data && (
              <div className="ml-auto text-sm text-[#575A57]" data-testid="haccp-totals">
                <strong className="text-[#A04E2C]">{data.totals.destroyed || 0}</strong> détruite(s) · <strong className="text-[#2E6A8E]">{data.totals.frozen || 0}</strong> congelée(s) · <strong className="text-[#1F4E3D]">{data.totals.staff || 0}</strong> don(s) personnel
              </div>
            )}
          </div>
          <div className="border border-[#E5E5E0] rounded overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF7]">
                  <TableHead>Date</TableHead><TableHead>Opération</TableHead><TableHead>Produit</TableHead>
                  <TableHead>Lot</TableHead><TableHead className="text-right">Qté</TableHead><TableHead>DLC</TableHead><TableHead>Détail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data && <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>}
                {data && !data.events.length && <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#575A57]" data-testid="haccp-empty">Aucune opération sur la période.</TableCell></TableRow>}
                {data && data.events.map((e) => {
                  const [tone, Icon] = TONE[e.type] || TONE.DESTRUCTION;
                  return (
                    <TableRow key={e.id} data-testid={`haccp-row-${e.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(e.date)}</TableCell>
                      <TableCell><Badge className={`${tone} text-[10px] gap-1`}><Icon className="w-3 h-3" /> {EVENT_LABEL[e.type]}</Badge></TableCell>
                      <TableCell className="text-sm">{e.product_name} <span className="text-xs text-[#575A57] font-mono">{e.product_sku}</span></TableCell>
                      <TableCell className="font-mono text-xs">{e.lot_number}</TableCell>
                      <TableCell className="text-right font-mono">{e.quantity}</TableCell>
                      <TableCell className="text-xs">{fmtDate(e.dlc)}</TableCell>
                      <TableCell className="text-xs text-[#575A57]">{detail(e)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
